import { useEffect, useId, useState } from "react";
import { canDocflow } from "@/auth/portal";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Building2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { loadEntities, saveEntityFn } from "@/braining/legal-entities.functions";
import {
  EMPTY_ENTITY,
  FIELD_LABEL,
  type EntityData,
  type EntityForm,
  type EntityProblems,
  type LegalEntity,
} from "@/braining/legal-entity";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * Юрлица агентства — мастер-запись реквизитов для документов.
 *
 * Встреча 14.10: «если у тебя поменялся счёт, просто во всех шаблонах
 * этого юрлица меняется счёт». Шаблоны появятся на этапе 4; реквизиты
 * заводятся сейчас, чтобы к тому времени было откуда брать.
 *
 * Сохранение не пропускает реквизиты, которые не сходятся: опечатка в
 * мастер-записи разъехалась бы по всем договорам сразу.
 */
export const Route = createFileRoute("/entities")({
  head: () => ({ meta: [{ title: "Юрлица агентства — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  component: EntitiesPage,
});

type Draft = { id?: string; name: string; data: EntityData };

function EntitiesPage() {
  // Правят мастер-записи администратор и руководитель (RLS legal_entities_write
  // то же); менеджер по документам их только смотрит.
  const { user } = Route.useRouteContext();
  const canEdit = user?.role === "admin" || user?.role === "rop";
  const [list, setList] = useState<LegalEntity[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setList(await loadEntities());
    } catch (e) {
      console.error("[entities] не удалось прочитать список", e);
      setError("Не удалось прочитать список юрлиц");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Юрлица агентства</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Реквизиты, от чьего имени заключаются договоры. Поправили здесь — поправится во всех
          документах.
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
          {error}
        </p>
      )}

      {list === null ? (
        <p className="text-sm text-muted-foreground">Загружаем…</p>
      ) : (
        <>
          {list.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground">
              Юрлиц пока нет. Заведите оба юрлица агентства — шаблоны договоров будут брать
              реквизиты отсюда.
            </p>
          )}
          {list.map((e) => (
            <EntityCard
              key={e.id}
              initial={{ id: e.id, name: e.name, data: e.data }}
              onSaved={refresh}
              readOnly={!canEdit}
            />
          ))}
          {!canEdit ? null : adding ? (
            <EntityCard
              initial={{ name: "", data: EMPTY_ENTITY }}
              onSaved={async () => {
                setAdding(false);
                await refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <Button variant="outline" onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              Добавить юрлицо
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** Порядок полей — как в разделе реквизитов договора. */
const LAYOUT: { key: Exclude<keyof EntityData, "form">; wide?: boolean; onlyCompany?: boolean }[] =
  [
    { key: "fullName", wide: true },
    { key: "inn" },
    { key: "kpp", onlyCompany: true },
    { key: "ogrn" },
    { key: "taxNote" },
    { key: "address", wide: true },
    { key: "bank", wide: true },
    { key: "bik" },
    { key: "account" },
    { key: "corrAccount" },
    { key: "signerName" },
    { key: "signerPosition" },
    { key: "signerBasis" },
    { key: "email" },
    { key: "phone" },
    { key: "postAddress", wide: true },
    { key: "okved" },
    { key: "edoId" },
    { key: "preamble", wide: true },
  ];

const PLACEHOLDER: Partial<Record<keyof EntityData, string>> = {
  signerBasis: "Устава / доверенности № … от …",
  taxNote: "НДС 22% / УСН, без НДС",
  preamble:
    "Общество с ограниченной ответственностью «…», в лице Генерального директора Ивановой Марии Петровны, действующей на основании Устава",
  postAddress: "115455, а/я 26",
  edoId: "2BM-…",
};

function EntityCard({
  initial,
  onSaved,
  onCancel,
  readOnly = false,
}: {
  initial: Draft;
  onSaved: () => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
}) {
  const uid = useId();
  const [draft, setDraft] = useState<Draft>(initial);
  const [problems, setProblems] = useState<EntityProblems>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const set = (key: keyof EntityData, value: string) =>
    setDraft((d) => ({ ...d, data: { ...d.data, [key]: value } }));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const res = await saveEntityFn({ data: draft });
      if (!res.ok) {
        setError(res.error);
        setProblems(res.problems ?? {});
        return;
      }
      setProblems({});
      toast.success(`«${draft.name.trim()}» сохранено`);
      await onSaved();
    } catch (e) {
      console.error("[entities] не удалось сохранить", e);
      setError("Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <fieldset disabled={readOnly} className="contents">
          <div className="flex flex-wrap items-end gap-3">
            <Building2 className="mb-2.5 size-4 text-muted-foreground" />
            <div className="min-w-[200px] flex-1 space-y-1.5">
              <Label htmlFor={`${uid}-name`} className="text-xs font-normal text-muted-foreground">
                Как называем внутри
              </Label>
              <Input
                id={`${uid}-name`}
                value={draft.name}
                placeholder="Брейнинг"
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            <div className="w-[170px] space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">Форма</Label>
              <Select
                value={draft.data.form}
                onValueChange={(v) =>
                  setDraft((d) => ({ ...d, data: { ...d.data, form: v as EntityForm } }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">ООО / юрлицо</SelectItem>
                  <SelectItem value="ip">ИП</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {LAYOUT.filter((f) => !(f.onlyCompany && draft.data.form === "ip")).map((f) => (
              <div key={f.key} className={cn("space-y-1.5", f.wide && "sm:col-span-2")}>
                <Label
                  htmlFor={`${uid}-${f.key}`}
                  className="text-xs font-normal text-muted-foreground"
                >
                  {f.key === "ogrn"
                    ? draft.data.form === "ip"
                      ? "ОГРНИП"
                      : "ОГРН"
                    : FIELD_LABEL[f.key]}
                </Label>
                <Input
                  id={`${uid}-${f.key}`}
                  value={draft.data[f.key]}
                  placeholder={PLACEHOLDER[f.key]}
                  aria-invalid={Boolean(problems[f.key])}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                {problems[f.key] && (
                  <p className="text-xs text-destructive-ink">{problems[f.key]}</p>
                )}
              </div>
            ))}
          </div>
        </fieldset>

        {error && <p className="text-sm text-destructive-ink">{error}</p>}

        {!readOnly && (
          <div className="flex gap-2">
            <Button
              onClick={() => void save()}
              disabled={busy || !draft.name.trim() || (!dirty && !!draft.id)}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              Сохранить
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Отмена
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
